
import { defineComponent, nextTick, PropType } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import InboxMessageBubble from "@/components/prospects/InboxMessageBubble.vue";
import {
  listPlaygroundMessages,
  sendPlaygroundMessage,
  resetPlaygroundConversation
} from "@/api/aiAgents.api.js";
import { getGhlAiUsage } from "@/api/ghlAi.api.js";
import { messageKey } from "@/utils/inboxMessageFormat.js";

interface PlaygroundMessage {
  id?: number;
  role: string;
  content: string;
  createdAt?: string | null;
}

interface PlaygroundAgent {
  id: number;
  name: string;
}

export default defineComponent({
  name: "ai-agent-playground",
  components: {
    InboxMessageBubble
  },
  props: {
    agent: {
      type: Object as PropType<PlaygroundAgent>,
      required: true
    }
  },
  emits: ["close"],
  data() {
    return {
      loading: true,
      sending: false,
      resetting: false,
      loadError: "",
      messages: [] as PlaygroundMessage[],
      draft: "",
      creditsRemaining: null as number | null,
      creditsDepleted: false
    };
  },
  watch: {
    "agent.id": {
      immediate: false,
      handler() {
        this.loadConversation();
      }
    }
  },
  async mounted() {
    await Promise.all([this.loadConversation(), this.loadCredits()]);
  },
  methods: {
    messageKey,
    formatNumber(value: number | null) {
      return new Intl.NumberFormat().format(Number(value) || 0);
    },
    async loadCredits() {
      try {
        const response = await getGhlAiUsage();
        const credits = response.data?.credits || {};
        this.creditsRemaining = Number(credits.displayCreditsRemaining) || 0;
        this.creditsDepleted = Boolean(credits.creditsDepleted);
      } catch {
        this.creditsRemaining = null;
        this.creditsDepleted = false;
      }
    },
    async loadConversation() {
      this.loading = true;
      this.loadError = "";
      try {
        const response = await listPlaygroundMessages(this.agent.id);
        this.messages = Array.isArray(response.data?.messages) ? response.data.messages : [];
        await nextTick();
        this.scrollToBottom();
      } catch (err) {
        this.loadError = (err as any)?.response?.data?.message || "Failed to load playground messages.";
        this.messages = [];
      } finally {
        this.loading = false;
      }
    },
    async sendMessage() {
      const text = this.draft.trim();
      if (!text || this.sending || this.creditsDepleted) {
        return;
      }

      this.sending = true;
      this.loadError = "";
      const optimistic: PlaygroundMessage = {
        role: "user",
        content: text,
        createdAt: new Date().toISOString()
      };
      this.messages = [...this.messages, optimistic];
      this.draft = "";
      await nextTick();
      this.scrollToBottom();

      try {
        const response = await sendPlaygroundMessage(this.agent.id, text);
        const returned = Array.isArray(response.data?.messages) ? response.data.messages : [];
        if (returned.length >= 2) {
          this.messages = [
            ...this.messages.slice(0, -1),
            ...returned
          ];
        } else if (response.data?.reply) {
          this.messages = [
            ...this.messages.slice(0, -1),
            optimistic,
            {
              role: "assistant",
              content: response.data.reply,
              createdAt: new Date().toISOString()
            }
          ];
        }
        if (response.data?.creditsRemaining != null) {
          this.creditsRemaining = Number(response.data.creditsRemaining) || 0;
          this.creditsDepleted = this.creditsRemaining <= 0;
        } else {
          await this.loadCredits();
        }
        await nextTick();
        this.scrollToBottom();
      } catch (err) {
        this.messages = this.messages.slice(0, -1);
        this.draft = text;
        const status = (err as any)?.response?.status;
        const message = (err as any)?.response?.data?.message || "Could not send playground message.";
        if (status === 429) {
          this.creditsDepleted = true;
          Swal.fire({
            icon: "warning",
            title: "Insufficient AI credits",
            text: message
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Playground error",
            text: message
          });
        }
      } finally {
        this.sending = false;
      }
    },
    async resetConversation() {
      const result = await Swal.fire({
        icon: "question",
        title: "Reset playground?",
        text: "This clears the test conversation for this agent.",
        showCancelButton: true,
        confirmButtonText: "Reset"
      });
      if (!result.isConfirmed) {
        return;
      }

      this.resetting = true;
      try {
        await resetPlaygroundConversation(this.agent.id);
        this.messages = [];
        this.draft = "";
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not reset playground",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      } finally {
        this.resetting = false;
      }
    },
    scrollToBottom() {
      const el = this.$refs.messagesRef as HTMLElement | undefined;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }
});
